import styled from "styled-components";
import {
  CheckboxWithLabel,
  IconButton,
  LabelAndValue,
  Button,
  Logo,
  Title,
  BookingSummary,
  ReturnTableNotice,
} from "../../components/index";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { State } from "../../types";
import { defaultThemeStyles } from "../../mocks";
import { useTranslation } from "react-i18next";
import DateHelper from "../../helpers/DateHelper";
import { ArrowLeftSolid } from "../../assets/icons";
import SelectedVenueDetailsHelper from "../../helpers/SelectedVenueDetailsHelper";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import WidgetService from "../../services/WidgetService";
import mockWidgetService from "../../mocks/mockWidgetService";
import * as locales from "date-fns/locale";

const StyledPageContainer = styled.div`
  display: grid;
  row-gap: 16px;
`;

const StyledHeader = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr 30px;
  justify-content: center;
  align-items: center;
`;

const StyledTitle = styled(Title)`
  text-align: center;
  flex-grow: 1;
`;

const CustomerDetails = styled.div``;

const Link = styled.span`
  text-decoration: underline;
  padding: 0px 2px;
  cursor: pointer;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export type ConfirmDetailsPageProps = {
  state: State;
  nextPage: (payload: Partial<State>) => void;
  prevPage: (payload: Partial<State>) => void;
  widgetService: WidgetService | mockWidgetService;
  setState: (payload: Partial<State>) => void;
  showTermsAndConditionsPage: () => void;
  showPrivacyPolicyPage: () => void;
};

export const ConfirmDetailsPage: React.FC<ConfirmDetailsPageProps> = ({
  state,
  nextPage,
  prevPage,
  widgetService,
  setState,
  showTermsAndConditionsPage,
  showPrivacyPolicyPage,
}) => {
  const { t } = useTranslation();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);

  useEffect(() => {
    const fetchTermsAndPrivacyPolicy = async () => {
      if (
        state.bookingDetails?.timeSlot?.MicrositeName !==
        state.activeTermsAndPrivacyPolicy?.MicrositeName
      ) {
        setShowSpinner(true);
        const termsAndPrivacyPolicyResponse =
          await widgetService.getTermsAndPrivacyPolicy(
            state.hostname || "",
            state.bookingDetails?.timeSlot?.MicrositeName || "",
            new Date(),
            state.channelCode || ""
          );
        setState({
          activeTermsAndPrivacyPolicy: termsAndPrivacyPolicyResponse,
        });
        setShowSpinner(false);
      }
    };

    if (state.isCrossSellChosen) {
      fetchTermsAndPrivacyPolicy();
    } else if (
      state.micrositeName !== state.activeTermsAndPrivacyPolicy?.MicrositeName
    ) {
      setState({
        activeTermsAndPrivacyPolicy: {
          MicrositeName: state.micrositeName || "",
          PrivacyPolicy: state.crossSellSetup?.PrivacyPolicy || "",
          TermsAndConditions: state.crossSellSetup?.TermsAndConditions || "",
        },
      });
    }
  }, [
    setState,
    state.activeTermsAndPrivacyPolicy?.MicrositeName,
    state.bookingDetails?.timeSlot?.MicrositeName,
    state.channelCode,
    state.crossSellSetup?.PrivacyPolicy,
    state.crossSellSetup?.TermsAndConditions,
    state.hostname,
    state.isCrossSellChosen,
    state.micrositeName,
    widgetService,
  ]);

  const backButtonClick = () => {
    prevPage(state);
  };

  const nextButtonClick = async () => {
    if (
      termsAccepted ||
      (!state.activeTermsAndPrivacyPolicy?.TermsAndConditions &&
        !state.activeTermsAndPrivacyPolicy?.PrivacyPolicy)
    ) {
      setShowSpinner(true);
      let bookingFunction;
      if (state.crossSellSetup?.UseConfirmationTokenStripeFlow) {
        bookingFunction = widgetService.createBookingWithConfirmationToken;
      } else {
        bookingFunction = widgetService.createBookingWithStripeToken;
      }
      await bookingFunction(
        state.hostname!,
        state.bookingDetails.timeSlot!.MicrositeName!,
        {
          ChannelCode: state.channelCode!,
          Customer: state.contactDetails!,
          Reference: undefined,
          RestaurantName: undefined,
          VisitDate: DateHelper.getDateString(
            state.bookingDetails.selectedDate
          ),
          VisitTime: DateHelper.sliceTimeSlot(state.bookingDetails.timeSlot!),
          PartySize: state.bookingDetails.partySize,
          SpecialRequests: state.contactDetails!.SpecialRequests,
          PromotionId: state.promotionDetails?.promotionId,
          IpAddress: undefined,
          IsLeaveTimeConfirmed:
            state.bookingDetails.timeSlot?.TimeSlot.IsLeaveTimeRequired,
          ConciergeId: undefined,
          ConciergeEmail: undefined,
          ConciergeCustomerId: undefined,
          Token: undefined,
          PaymentInformation: undefined,
          AreaId: undefined,
          PaymentMethodId: null,
          PaymentIntentId: null,
          SetupIntentId: null,
          RoomNumber: undefined,
          PreorderGuestFormUrl: undefined,
          DeliveryAddress: undefined,
          Households: state.contactDetails?.Households,
          IsWidgetBooking: true,
          MicrositeName: state.bookingDetails.timeSlot!.MicrositeName!,
          OriginalMicrositeName: state.micrositeName!,
          WidgetThemeId: state.themeId!,
          ConfirmationTokenId: undefined,
        }
      ).then((response) => {
        nextPage({ ...state, bookingConfirmationDetails: response });
        setShowSpinner(false);
      });
    } else {
      setTermsError(true);
    }
  };

  const formatDate = (date: Date) => {
    // Format the date using the user's (or fallback to restaurant's) locale, 'P' represents the locale-specific date format
    const locale =
      navigator.language || state.crossSellSetup?.CultureName || "en-GB";
    return format(date, "P", {
      locale: Object.entries(locales).find(
        (entry) => entry[1].code === locale
      )?.[1],
    });
  };

  const handleCheckboxChange = () => {
    setTermsAccepted(!termsAccepted);
    setTermsError(false);
  };

  const data = {
    [t("Date")]: state.bookingDetails.selectedDate,
    [t("Time")]: DateHelper.sliceTimeSlot(state.bookingDetails.timeSlot!),
    [t("People")]: state.bookingDetails.partySize,
    [t("Name")]:
      `${state.contactDetails!.Title ? `${state.contactDetails!.Title as string} ` : ""}` +
      state.contactDetails!.FirstName +
      " " +
      state.contactDetails!.Surname,
    ...(!!state.contactDetails!.Company && {
      [t("Company")]: state.contactDetails!.Company as string,
    }),
    ...(!!state.contactDetails!.Phone && {
      [t("TelephoneNumber")]: state.contactDetails!.Phone as string,
    }),
    [t("MobileNumber")]: state.contactDetails!.Mobile,
    [t("Email")]: state.contactDetails!.Email,
    ...(!!state.contactDetails!.MembershipId && {
      [state.crossSellSetup?.ThemeSettings?.MembershipIdTitle ||
      t("MembershipId")]: state.contactDetails!.MembershipId as string,
    }),
    ...(!!state.contactDetails!.Postcode && {
      [t("Postcode")]: state.contactDetails!.Postcode as string,
    }),
    ...(!!state.contactDetails?.CustomField && {
      [state.customField!.Name!]: state.contactDetails.CustomField ?? "",
    }),
  };

  return (
    <StyledPageContainer>
      <Logo styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles} />
      {!showSpinner && (
        <>
          <StyledHeader>
            <IconButton
              icon={ArrowLeftSolid}
              ariaLabel={t("Previous")}
              onClick={backButtonClick}
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              dataId="back-button"
            />
            <StyledTitle
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
            >
              {t("ConfirmDetails")}
            </StyledTitle>
          </StyledHeader>
          <CustomerDetails>
            {Object.entries(data).map(([fieldName, value]) => (
              <LabelAndValue
                key={fieldName}
                fieldName={fieldName}
                styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
                dataId={fieldName.toLowerCase().replace(" ", "-")}
              >
                {fieldName === t("Date") && value instanceof Date
                  ? formatDate(value)
                  : String(value)}
              </LabelAndValue>
            ))}
          </CustomerDetails>
          {(state.activeTermsAndPrivacyPolicy?.TermsAndConditions ||
            state.activeTermsAndPrivacyPolicy?.PrivacyPolicy) && (
            <CheckboxWithLabel
              onChecked={handleCheckboxChange}
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              {...(termsError && { errorMessage: t("Required") })}
              dataId="terms-and-conditions"
            >
              <label>
                {t("AcceptTermsConditions")}{" "}
                {state.activeTermsAndPrivacyPolicy?.TermsAndConditions && (
                  <Link
                    role="link"
                    tabIndex={0}
                    onClick={showTermsAndConditionsPage}
                    data-id="terms-and-conditions-link"
                  >
                    {t("RestaurantTermsTitle")}
                  </Link>
                )}
                {state.activeTermsAndPrivacyPolicy?.TermsAndConditions &&
                  state.activeTermsAndPrivacyPolicy?.PrivacyPolicy && (
                    <>{" " + t("AndPrivacyPolicy") + " "} </>
                  )}
                {state.activeTermsAndPrivacyPolicy?.PrivacyPolicy && (
                  <Link
                    role="link"
                    tabIndex={0}
                    onClick={showPrivacyPolicyPage}
                    data-id="privacy-policy-link"
                  >
                    {t("RestaurantPrivacyPolicyLink")}
                  </Link>
                )}
              </label>
            </CheckboxWithLabel>
          )}
          <FooterContainer>
            <BookingSummary
              flashBookingSummaryPanel={state.flashBookingSummaryPanel || false}
              date={state.bookingDetails.selectedDate}
              numberOfCovers={state.bookingDetails.partySize}
              timeslot={
                state.bookingDetails.timeSlot
                  ? DateHelper.sliceTimeSlot(state.bookingDetails.timeSlot)
                  : null
              }
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              {...(state.isCrossSellChosen && {
                newProvider: {
                  name:
                    SelectedVenueDetailsHelper.getVenueDetails(state).name ||
                    "",
                  location:
                    SelectedVenueDetailsHelper.getVenueDetails(state)
                      .location || "",
                  locationUrl:
                    SelectedVenueDetailsHelper.getVenueDetails(state).url || "",
                },
              })}
            />
            {state.bookingDetails.timeSlot?.TimeSlot.IsLeaveTimeRequired &&
              state.bookingDetails.timeSlot?.TimeSlot.LeaveTime && (
                <ReturnTableNotice
                  styles={
                    state.crossSellSetup?.ThemeStyles || defaultThemeStyles
                  }
                  leaveTime={
                    state.promotionDetails?.leaveTime.length
                      ? state.promotionDetails.leaveTime
                      : state.bookingDetails.timeSlot?.TimeSlot.LeaveTime
                  }
                  isPromotion={!!state.promotionDetails?.leaveTime.length}
                />
              )}
            <Button
              styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
              onClick={nextButtonClick}
              rightArrowIcon
              dataId="next-button"
            >
              {t("Next")}
            </Button>
          </FooterContainer>
        </>
      )}
      {showSpinner && <LoadingSpinner hostname={state.hostname ?? ""} />}
    </StyledPageContainer>
  );
};

export default ConfirmDetailsPage;
